import { createContext, type ReactNode, useContext, useMemo } from "react";
import { hasDisabledReason } from "../../lib/magicFieldDisabledReason";

/** What the enclosing `MagicFieldset`s say about the magic fields inside. */
export interface MagicFieldsetState {
  /** Whether an enclosing fieldset disables the fields. */
  disabled: boolean;
  /**
   * Why: the reason of the innermost disabled fieldset that has one. Only set
   * while `disabled` is true.
   */
  reason?: ReactNode;
}

const enabledFieldset: MagicFieldsetState = { disabled: false };

const MagicFieldsetContext = createContext<MagicFieldsetState>(enabledFieldset);

/** Whether an enclosing `MagicFieldset` disables the magic fields inside it. */
export function useMagicFieldsetDisabled(): boolean {
  return useContext(MagicFieldsetContext).disabled;
}

/** Whether and why the enclosing `MagicFieldset`s disable the fields inside. */
export function useMagicFieldsetState(): MagicFieldsetState {
  return useContext(MagicFieldsetContext);
}

export interface MagicFieldsetProviderProps {
  /** Disables every magic field inside. A nested provider can only add to it. */
  disabled?: boolean;
  /**
   * Shown as a tooltip on the fields inside while `disabled` is set. It wins
   * over the reason of an enclosing fieldset.
   */
  disabledReason?: ReactNode;
  children: ReactNode;
}

/**
 * Disables the magic fields inside, on top of their permission check. A
 * fieldset can only restrict: inside a disabled fieldset, a nested one with
 * `disabled={false}` neither enables anything nor hides the outer fieldset's
 * reason. The skin modules wrap this in their `MagicFieldset` component.
 */
export function MagicFieldsetProvider({
  disabled = false,
  disabledReason,
  children,
}: Readonly<MagicFieldsetProviderProps>) {
  const parent = useMagicFieldsetState();
  const ownReason = disabled && hasDisabledReason(disabledReason);
  const value = useMemo<MagicFieldsetState>(
    () => ({
      disabled: parent.disabled || disabled,
      reason: ownReason ? disabledReason : parent.reason,
    }),
    [parent, disabled, ownReason, disabledReason],
  );
  return (
    <MagicFieldsetContext.Provider value={value}>
      {children}
    </MagicFieldsetContext.Provider>
  );
}
